import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, ArrowLeft, Download, Loader2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useGuidesStore } from '../stores/guidesStore';
import { usePilgrimGuideStore } from '../stores/pilgrimGuideStore';
import * as XLSX from 'xlsx';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { normalizePassportNumber } from '../utils/passportUtils';
import toast from 'react-hot-toast';

type ProcessingResult = {
  identifier: string;
  pilgrimId?: string;
  pilgrimName?: string;
  guide_name?: string;
  guide_passport?: string;
  is_owner?: boolean; // New field for ownership status
  status: 'success' | 'error' | 'processing';
  errors: string[];
};

const ITEMS_PER_PAGE_KEY = 'bulk_link_items_per_page';
const PAGE_SIZE_OPTIONS = [10, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

export const BulkLinkStatusPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { translations } = useLanguage();
  const [results, setResults] = useState<ProcessingResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const {
    guides,
    initialized: guidesInitialized,
    initializeListener: initializeGuides,
  } = useGuidesStore();
  const { getPilgrimDetails, linkPilgrimToGuide, linkWaitingIdentification } = usePilgrimGuideStore();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const savedSize = localStorage.getItem(ITEMS_PER_PAGE_KEY);
    return savedSize ? parseInt(savedSize) : DEFAULT_PAGE_SIZE;
  });

  // Save items per page preference
  useEffect(() => {
    localStorage.setItem(ITEMS_PER_PAGE_KEY, itemsPerPage.toString());
  }, [itemsPerPage]);

  // Calculate pagination
  const totalPages = Math.ceil(results.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResults = results.slice(startIndex, startIndex + itemsPerPage);

  const handleItemsPerPageChange = (size: number) => {
    setItemsPerPage(size);
    setCurrentPage(1); // Reset to first page when changing items per page
    localStorage.setItem(ITEMS_PER_PAGE_KEY, size.toString());
  };

  useEffect(() => {
    const processFile = async () => {
      const state = location.state as { fileData?: string };
      if (!state?.fileData) {
        navigate('/pilgrim-guide');
        return;
      }

      try {
        setIsProcessing(true);
        const workbook = XLSX.read(state.fileData, { type: 'binary' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(worksheet);
        
        setTotalRows(rows.length);
        setResults(rows.map((_, index) => ({
          identifier: (rows[index] as any).identifier || 'Unknown',
          status: 'processing' as const,
          errors: []
        })));

        for (let i = 0; i < rows.length; i++) {
          const row = rows[i] as any;
          const result = await validateAndProcessRow(row, i);
          
          setCurrentProgress(i + 1);
          setResults(prev => {
            const newResults = [...prev];
            newResults[i] = result;
            return newResults;
          });

          // Add delay between rows to prevent race conditions
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        // After processing, set to last page
        const newTotalPages = Math.ceil(rows.length / itemsPerPage);
        setCurrentPage(newTotalPages);
      } catch (error: any) {
        console.error('Error processing file:', error);
        setResults(prev => [...prev, {
          identifier: 'File Error',
          status: 'error',
          errors: [error.message || 'Error processing file']
        }]);
      } finally {
        setIsProcessing(false);
      }
    };

    processFile();
  }, [location.state, navigate, itemsPerPage]);

  useEffect(() => {
    if (!guidesInitialized) {
      const unsubscribe = initializeGuides();
      return unsubscribe;
    }
  }, [guidesInitialized, initializeGuides]);

  const guideCache = React.useRef(new Map<string, any>());
  const passportCache = React.useRef(new Map<string, any>());
  const normalizeGuideName = (value: string) =>
    String(value || '')
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '')
      .toLowerCase();

  const getGuideDisplay = (result: ProcessingResult) => {
    if (!result.guide_name) return '-';
    if (!result.guide_passport) return result.guide_name;
    return `${result.guide_name} (${result.guide_passport})`;
  };

  const validateAndProcessRow = async (row: any, rowIndex: number): Promise<ProcessingResult> => {
    const result: ProcessingResult = {
      identifier: row.identifier || 'Unknown',
      status: 'processing',
      errors: [],
      is_owner: row.is_owner !== 'false' // Default to true unless explicitly set to 'false'
    };
    const guideNameRaw = String(row.guide_name || '').trim();
    const guidePassportRaw = String(row.guide_passport || '').trim();
    if (guideNameRaw) {
      result.guide_name = guideNameRaw;
    }
    if (guidePassportRaw) {
      result.guide_passport = guidePassportRaw;
    }
    console.log('[BulkLink] Row start', {
      rowIndex,
      identifier: row.identifier,
      guide_name: guideNameRaw,
      guide_passport: guidePassportRaw,
      userRole,
      userId,
      guidesCount: Object.keys(guides).length
    });

    try {
      // Validate identifier type
      if (!row.identifier_type || !['passport'].includes(row.identifier_type.toLowerCase())) {
        throw new Error('Invalid identifier type - only passport is supported');
      }

      // Validate identifier
      if (!row.identifier) {
        throw new Error('Identifier is required');
      }

      // Validate guide info for non-guide users
      if (userRole !== 'guide' && !guideNameRaw && !guidePassportRaw) {
        throw new Error('Guide name or passport is required');
      }

      // Get guide ID
      let guideId = userId;
      let organizerId = userId;
      if (guideId && guides[guideId]) {
        result.guide_name = guides[guideId]?.name;
        result.guide_passport = guides[guideId]?.passportNumber;
      }

      if (userRole !== 'guide') {
        let resolvedGuide: any = null;

        if (guidePassportRaw) {
          const guidePassportKey = normalizePassportNumber(guidePassportRaw).toUpperCase();
          console.log('[BulkLink] Guide passport normalize', {
            guidePassportRaw,
            guidePassportKey
          });
          const cachedGuide = passportCache.current.get(guidePassportKey);
          if (cachedGuide) {
            console.log('[BulkLink] Guide passport cache hit', {
              guideId: cachedGuide.id,
              guideName: cachedGuide.name,
              guidePassport: cachedGuide.passportNumber ?? cachedGuide.passport_number
            });
            resolvedGuide = cachedGuide;
          } else {
            resolvedGuide =
              Object.values(guides).find(guide => {
                const passportValue = guide.passportNumber ?? guide.passport_number ?? '';
                return normalizePassportNumber(passportValue).toUpperCase() === guidePassportKey;
              }) || null;

            console.log('[BulkLink] Guide passport store match', {
              found: !!resolvedGuide,
              guidePassportRaw,
              guidePassportKey
            });

            if (!resolvedGuide && guidePassportRaw) {
              const guidesRef = collection(firestore, 'Guides');
              const queryValues = Array.from(
                new Set([guidePassportRaw, guidePassportKey, guidePassportKey.toLowerCase()])
              );
              for (const value of queryValues) {
                if (!value) continue;
                if (userRole === 'organizer' && userId) {
                  const organizerQuery = query(
                    guidesRef,
                    where('organizerId', '==', userId),
                    where('passportNumber', '==', value)
                  );
                  const organizerSnapshot = await getDocs(organizerQuery);
                  if (!organizerSnapshot.empty) {
                    resolvedGuide = {
                      id: organizerSnapshot.docs[0].id,
                      ...organizerSnapshot.docs[0].data(),
                    };
                    break;
                  }
                }
                const guideQuery = query(guidesRef, where('passportNumber', '==', value));
                const guideSnapshot = await getDocs(guideQuery);
                if (!guideSnapshot.empty) {
                  resolvedGuide = {
                    id: guideSnapshot.docs[0].id,
                    ...guideSnapshot.docs[0].data(),
                  };
                  console.warn('[BulkLink] Guide passport matched outside organizer scope', {
                    guidePassportRaw: value,
                    userId
                  });
                  break;
                }
              }
              console.log('[BulkLink] Guide passport Firestore match', {
                found: !!resolvedGuide,
                guidePassportRaw
              });
            }
          }

          if (resolvedGuide) {
            passportCache.current.set(guidePassportKey, resolvedGuide);
          }
        }

        if (!resolvedGuide && guideNameRaw) {
          const guideNameKey = normalizeGuideName(guideNameRaw);
          console.log('[BulkLink] Guide normalize', {
            guideNameRaw,
            guideNameKey
          });
          const cachedGuide = guideCache.current.get(guideNameKey);
          if (cachedGuide) {
            console.log('[BulkLink] Guide cache hit', {
              guideId: cachedGuide.id,
              guideName: cachedGuide.name,
              guidePassport: cachedGuide.passportNumber ?? cachedGuide.passport_number
            });
            resolvedGuide = cachedGuide;
          } else {
            resolvedGuide =
              Object.values(guides).find(
                g => normalizeGuideName(String(g.name || '')) === guideNameKey
              ) || null;
            console.log('[BulkLink] Guide store match', {
              found: !!resolvedGuide,
              guideNameRaw,
              guideNameKey,
              availableGuideNames: Object.values(guides).map(g => g.name)
            });

            if (!resolvedGuide && guideNameRaw) {
              const guidesRef = collection(firestore, 'Guides');
              const guideQuery =
                userRole === 'organizer' && userId
                  ? query(
                      guidesRef,
                      where('organizerId', '==', userId),
                      where('name', '==', guideNameRaw)
                    )
                  : query(guidesRef, where('name', '==', guideNameRaw));
              const guideSnapshot = await getDocs(guideQuery);
              if (!guideSnapshot.empty) {
                resolvedGuide = {
                  id: guideSnapshot.docs[0].id,
                  ...guideSnapshot.docs[0].data(),
                };
              }
              console.log('[BulkLink] Guide Firestore exact match', {
                found: !guideSnapshot.empty,
                guideNameRaw
              });
            }

            if (!resolvedGuide && userRole === 'organizer' && userId) {
              const guidesRef = collection(firestore, 'Guides');
              const organizerGuidesQuery = query(
                guidesRef,
                where('organizerId', '==', userId)
              );
              const organizerGuidesSnapshot = await getDocs(organizerGuidesQuery);
              const organizerGuides = organizerGuidesSnapshot.docs.map(docSnapshot => ({
                id: docSnapshot.id,
                ...docSnapshot.data(),
              }));
              resolvedGuide =
                organizerGuides.find(
                  guide => normalizeGuideName(String(guide.name || '')) === guideNameKey
                ) || null;
              console.log('[BulkLink] Guide organizer scan', {
                found: !!resolvedGuide,
                organizerGuidesCount: organizerGuides.length
              });
            }

            if (!resolvedGuide && userRole === 'organizer' && userId) {
              const guidesRef = collection(firestore, 'Guides');
              const guideQuery = query(
                guidesRef,
                where('organizerId', '==', userId),
                where('nameNormalized', '==', guideNameKey)
              );
              const guideSnapshot = await getDocs(guideQuery);
              if (!guideSnapshot.empty) {
                resolvedGuide = {
                  id: guideSnapshot.docs[0].id,
                  ...guideSnapshot.docs[0].data(),
                };
              }
            }
          }

          if (resolvedGuide) {
            guideCache.current.set(guideNameKey, resolvedGuide);
          }
        }

        if (!resolvedGuide) {
          console.warn('[BulkLink] Guide not found', {
            guideNameRaw,
            guidePassportRaw,
            userRole,
            userId
          });
          throw new Error('Guide not found');
        }

        guideId = resolvedGuide.id;
        organizerId = resolvedGuide.organizerId;
        if (userRole === 'organizer' && userId) {
          if (organizerId && organizerId !== userId) {
            console.warn('[BulkLink] Organizer mismatch, overriding organizerId', {
              guideId,
              guideOrganizerId: organizerId,
              userId
            });
          }
          organizerId = userId;
        }
        result.guide_name = resolvedGuide.name ?? result.guide_name;
        result.guide_passport =
          resolvedGuide.passportNumber ?? resolvedGuide.passport_number ?? result.guide_passport;
      }

      // Get pilgrim details
      let pilgrimDetails;
      try {
        pilgrimDetails = await getPilgrimDetails(
          row.identifier,
          'passport' as 'passport' | 'email',
          translations
        );
      } catch (error: any) {
        if (error?.code === 'PILGRIM_NOT_FOUND' && userRole === 'organizer') {
          await linkWaitingIdentification(
            row.identifier,
            guideId!,
            organizerId!,
            translations,
            result.is_owner
          );

          result.status = 'success';
          result.pilgrimName =
            translations?.linkPilgrimToGuideAction?.waitingIdentificationName ||
            'Waiting Identification';
          result.guide_name = guides[guideId!]?.name || result.guide_name || row.guide_name;
          result.guide_passport =
            guides[guideId!]?.passportNumber ?? result.guide_passport;
          return result;
        }
        throw new Error(error.message);
      }

      if (!pilgrimDetails) {
        throw new Error('Pilgrim not found');
      }

      // Link pilgrim to guide
      await linkPilgrimToGuide(
        pilgrimDetails.pilgrimId, 
        guideId!, 
        organizerId!, 
        translations,
        result.is_owner // Pass ownership status
      );

      result.status = 'success';
      result.pilgrimId = pilgrimDetails.pilgrimId;
      result.pilgrimName = pilgrimDetails.pilgrimName;
      result.guide_name = guides[guideId!]?.name || result.guide_name || row.guide_name;
      result.guide_passport =
        guides[guideId!]?.passportNumber ?? result.guide_passport;
    } catch (error: any) {
      result.status = 'error';
      result.errors.push(error.message);
    }

    return result;
  };

  const handleExport = () => {
    try {
      const exportData = results.map(result => ({
        'Identifier': result.identifier,
        'Pilgrim Name': result.pilgrimName || '-',
        'Guide': getGuideDisplay(result),
        'Is Owner': result.is_owner === false ? 'No' : 'Yes',
        'Status': result.status === 'success' ? 'Success' : 'Failed',
        'Errors': result.errors.join(', ') || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 20 }, // Identifier
        { wch: 30 }, // Pilgrim Name
        { wch: 25 }, // Guide
        { wch: 10 }, // Is Owner
        { wch: 15 }, // Status
        { wch: 50 }, // Errors
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bulk Link Results');

      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      const filename = `bulk-link-results-${date}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error('Error exporting results:', error);
    }
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;
  const processingCount = results.filter(r => r.status === 'processing').length;
  const successRate = results.length > 0 ? Math.round((successCount / results.length) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">
            {translations?.bulkLinkStatus?.title || 'Bulk Link Results'}
          </h1>
          <button
            onClick={() => navigate('/pilgrim-guide')}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft size={20} />
            <span>{translations?.bulkLinkStatus?.backToGuide || 'Back to Guide Page'}</span>
          </button>
        </div>

        {results.length > 0 && processingCount === 0 && (
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
          >
            <Download size={20} />
            <span>{translations?.bulkLinkStatus?.export || 'Export Results'}</span>
          </button>
        )}
      </div>

      {/* Processing Status */}
      {isProcessing && (
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
            <h2 className="text-lg font-medium">
              {translations?.bulkLinkStatus?.processing || 'Processing File...'}
            </h2>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentProgress / totalRows) * 100}%` }}
            />
          </div>
          <div className="mt-2 text-sm text-gray-600">
            {currentProgress} / {totalRows} {translations?.bulkLinkStatus?.rowsProcessed || 'rows processed'}
          </div>
        </div>
      )}

      {/* Stats */}
      {results.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-xl shadow-sm"
            >
              <div className="text-sm text-gray-600 mb-1">
                {translations?.bulkLinkStatus?.totalProcessed || 'Total Processed'}
              </div>
              <div className="text-2xl font-bold text-primary-600">
                {results.length}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-6 rounded-xl shadow-sm"
            >
              <div className="text-sm text-gray-600 mb-1">
                {translations?.bulkLinkStatus?.successful || 'Successful'}
              </div>
              <div className="text-2xl font-bold text-green-600">
                {successCount}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-6 rounded-xl shadow-sm"
            >
              <div className="text-sm text-gray-600 mb-1">
                {translations?.bulkLinkStatus?.failed || 'Failed'}
              </div>
              <div className="text-2xl font-bold text-red-600">
                {errorCount}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white p-6 rounded-xl shadow-sm"
            >
              <div className="text-sm text-gray-600 mb-1">
                {translations?.bulkLinkStatus?.successRate || 'Success Rate'}
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {successRate}%
              </div>
            </motion.div>
          </div>

          {/* Results Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkLinkStatus?.tableHeaders?.identifier || 'Identifier'}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkLinkStatus?.tableHeaders?.pilgrimName || 'Pilgrim Name'}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkLinkStatus?.tableHeaders?.guide || 'Guide'}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkLinkStatus?.tableHeaders?.isOwner || 'Is Owner'}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkLinkStatus?.tableHeaders?.status || 'Status'}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                      {translations?.bulkLinkStatus?.tableHeaders?.errors || 'Errors'}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedResults.map((result, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 text-sm">
                        {result.identifier}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {result.pilgrimName || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {getGuideDisplay(result)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {result.is_owner === false ? 'No' : 'Yes'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium ${
                          result.status === 'success'
                            ? 'bg-green-100 text-green-800'
                            : result.status === 'error'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {result.status === 'success' ? (
                            <CheckCircle size={14} />
                          ) : result.status === 'error' ? (
                            <XCircle size={14} />
                          ) : (
                            <Loader2 className="animate-spin" size={14} />
                          )}
                          {result.status === 'success'
                            ? translations?.bulkLinkStatus?.statusSuccess || 'Success'
                            : result.status === 'error'
                            ? translations?.bulkLinkStatus?.statusError || 'Failed'
                            : translations?.bulkLinkStatus?.statusProcessing || 'Processing'
                          }
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-red-600">
                        {result.errors.join(', ') || '-'}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t">
              <div className="flex items-center gap-4">
                {/* Items per page selector */}
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                  className="px-2 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                >
                  {PAGE_SIZE_OPTIONS.map(size => (
                    <option key={size} value={size}>
                      {size} {translations?.pagination?.perPage || 'per page'}
                    </option>
                  ))}
                </select>

                {/* Pagination controls */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={`p-1 rounded-lg ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <ChevronRight size={20} />
                  </button>

                  <span className="text-sm text-gray-700">
                    {translations?.pagination?.page || 'Page'} {currentPage}{' '}
                    {translations?.pagination?.of || 'of'} {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className={`p-1 rounded-lg ${
                      currentPage === totalPages
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <ChevronLeft size={20} />
                  </button>
                </div>
              </div>

              <div className="text-sm text-gray-500">
                {translations?.pagination?.showing || 'Showing'} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, results.length)} {translations?.pagination?.of || 'of'} {results.length}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
